import { FC, useRef, useEffect, useState } from 'react'
import styles from './ComparisonTable.module.scss'
import { Type } from 'components/Type'
import { Icon } from 'components/Phantom/Icon'

const Checkmark = () => (
	<Icon
		name="CheckLight"
		color="#000"
		className={styles.checkmark}
	/>
)

type ComparisonRowData = {
	feature: string
	sleepElixir: boolean
	magnesiumGlycinate: boolean
	magnesiumLThreonate: boolean
	melatonin: boolean
}

const comparisonData: ComparisonRowData[] = [
	{
		feature: 'Feel calmer at bedtime',
		sleepElixir: true,
		magnesiumGlycinate: true,
		magnesiumLThreonate: true,
		melatonin: false,
	},
	{
		feature: 'Calm racing thoughts',
		sleepElixir: true,
		magnesiumGlycinate: false,
		magnesiumLThreonate: false,
		melatonin: false,
	},
	{
		feature: 'Fall asleep faster',
		sleepElixir: true,
		magnesiumGlycinate: true,
		magnesiumLThreonate: true,
		melatonin: true,
	},
	{
		feature: 'Stay asleep longer',
		sleepElixir: true,
		magnesiumGlycinate: false,
		magnesiumLThreonate: false,
		melatonin: true,
	},
	{
		feature: 'No midnight wakeups',
		sleepElixir: true,
		magnesiumGlycinate: false,
		magnesiumLThreonate: false,
		melatonin: false,
	},
	{
		feature: 'Improve deep sleep',
		sleepElixir: true,
		magnesiumGlycinate: false,
		magnesiumLThreonate: false,
		melatonin: false,
	},
	{
		feature: 'Improve REM sleep',
		sleepElixir: true,
		magnesiumGlycinate: true,
		magnesiumLThreonate: true,
		melatonin: true,
	},
	{
		feature: 'Non-habit forming',
		sleepElixir: true,
		magnesiumGlycinate: true,
		magnesiumLThreonate: true,
		melatonin: false,
	},
]

const TableRow: FC<{ data: ComparisonRowData }> = ({ data }) => (
	<tr>
		<td>
			<Type.Body1 className={styles.table_body_text}>{data.feature}</Type.Body1>
		</td>
		<td>{data.sleepElixir && <Checkmark />}</td>
		<td>{data.magnesiumGlycinate && <Checkmark />}</td>
		<td>{data.magnesiumLThreonate && <Checkmark />}</td>
		<td>{data.melatonin && <Checkmark />}</td>
	</tr>
)

export const ComparisonTable: FC = () => {
	const sleepElixirCellRef = useRef<HTMLTableCellElement>(null)
	const tableWrapperRef = useRef<HTMLDivElement>(null)
	const [gradientStyle, setGradientStyle] = useState<{ left: number; width: number } | null>(null)
	const [isGradientVisible, setIsGradientVisible] = useState(false)

	useEffect(() => {
		const updateGradientPosition = () => {
			if (!sleepElixirCellRef.current) return

			const cell = sleepElixirCellRef.current
			const tableRect = cell.closest('table')?.getBoundingClientRect()
			const cellRect = cell.getBoundingClientRect()

			if (tableRect) {
				const left = cellRect.left - tableRect.left
				setGradientStyle({
					left,
					width: cellRect.width,
				})
			}
		}

		updateGradientPosition()
		window.addEventListener('resize', updateGradientPosition)
		return () => window.removeEventListener('resize', updateGradientPosition)
	}, [])

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setIsGradientVisible(true)
					}
				})
			},
			{
				threshold: 0.7, // Trigger when 70% of the element is visible
			}
		)

		if (tableWrapperRef.current) {
			observer.observe(tableWrapperRef.current)
		}

		return () => observer.disconnect()
	}, [])

	return (
		<section className={styles.container}>
			<div className={styles.table_header}>
				<header>
					<Type.Headline2
						animateOnScroll
						className={styles.header_title}
					>
						Your complete sleep solution
					</Type.Headline2>
					<Type.Body1
						animateOnScroll
						className={styles.header_subtitle}
					>
						Members report sleep support that goes further
					</Type.Body1>
				</header>
			</div>

			<div
				className={styles.table_wrapper}
				ref={tableWrapperRef}
			>
				{gradientStyle && (
					<div
						className={`${styles.gradient_overlay} ${isGradientVisible ? styles.visible : ''}`}
						style={{
							left: gradientStyle.left,
							width: gradientStyle.width,
						}}
					/>
				)}
				<table className={styles.table}>
					<thead>
						<tr>
							<th></th>
							<th ref={sleepElixirCellRef}>
								<Type.Headline5 className={styles.table_header_text}>Sleep Elixir</Type.Headline5>
							</th>
							<th>
								<Type.Headline5 className={styles.table_header_text}>Magnesium Glycinate</Type.Headline5>
							</th>
							<th>
								<Type.Headline5 className={styles.table_header_text}>Magnesium L-threonate</Type.Headline5>
							</th>
							<th>
								<Type.Headline5 className={styles.table_header_text}>Melatonin</Type.Headline5>
							</th>
						</tr>
					</thead>
					<tbody>
						{comparisonData.map((rowData, index) => (
							<TableRow
								key={index}
								data={rowData}
							/>
						))}
					</tbody>
				</table>
			</div>
		</section>
	)
}
